import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL =
  "https://static.readdy.ai/image/630c6b1470b412c02a23d556f94f9bee/4d2e9b251244fac3d3a565feae080dc8.webp";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const isServices = location.pathname === "/services";
  const isContact = location.pathname === "/contact";

  const quoteHref = isHome
    ? "#contact-us"
    : isServices
    ? "#quote-form"
    : isContact
    ? "#contact-us"
    : "/#contact-us";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? "bg-white shadow-sm border-b border-gray-100"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="Mighty Cleaning Services Ltd logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <span
                  className={`font-heading font-700 text-base leading-tight block transition-colors ${
                    scrolled || !isHome ? "text-[#1A3A6B]" : "text-white"
                  }`}
                  style={{ fontWeight: 700 }}
                >
                  Mighty Cleaning
                </span>
                <span
                  className={`font-body text-xs transition-colors ${
                    scrolled || !isHome ? "text-gray-500" : "text-blue-100"
                  }`}
                >
                  Services Ltd
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                const active = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-body font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      active
                        ? scrolled || !isHome
                          ? "text-[#1E88E5]"
                          : "text-white font-semibold"
                        : scrolled || !isHome
                        ? "text-gray-600 hover:text-[#1A3A6B]"
                        : "text-blue-100 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right Side */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:02084322244"
                className={`flex items-center gap-2 font-body font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  scrolled || !isHome
                    ? "text-[#1A3A6B] hover:text-[#1E88E5]"
                    : "text-white hover:text-blue-200"
                }`}
              >
                <div
                  className={`w-7 h-7 flex items-center justify-center rounded-full ${
                    scrolled || !isHome ? "bg-[#EBF4FF]" : "bg-white/20"
                  }`}
                >
                  <i className="ri-phone-fill text-xs text-[#1E88E5]"></i>
                </div>
                0208 43 222 44
              </a>
              <a
                href={quoteHref}
                className="bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Free Quote
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                scrolled || !isHome
                  ? "text-[#1A3A6B] hover:bg-gray-100"
                  : "text-white hover:bg-white/20"
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="font-body font-medium text-sm text-gray-700 hover:text-[#1E88E5] py-3 border-b border-gray-50 cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:02084322244"
                className="flex items-center gap-2 font-body font-semibold text-sm text-[#1A3A6B] py-3 cursor-pointer"
              >
                <i className="ri-phone-fill text-[#1E88E5]"></i>
                0208 43 222 44
              </a>
              <a
                href={quoteHref}
                className="mt-2 bg-[#1E88E5] text-white text-center font-heading font-semibold text-sm px-5 py-3 rounded-lg cursor-pointer"
              >
                Get a Free Quote
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Sticky floating CTA — mobile only */}
      <a
        href={quoteHref}
        className="md:hidden fixed bottom-6 right-5 z-40 flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-5 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
        style={{ boxShadow: "0 4px 20px rgba(30,136,229,0.45)" }}
      >
        <i className="ri-file-list-3-line text-base"></i>
        Get a Free Quote
      </a>
    </>
  );
}
